import { AxiosResponse } from 'axios'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { getType } from 'typesafe-actions'

import { apiCall } from '../../../utils/api'
import { waitForConfigLoad } from '../../../utils/sagaUtils'
import { Dictionary } from '../../../utils/types'
import { getAccountReportsPageSize } from '../../config'
import { withLoading } from '../../loading'
import { ColumnSorting, TableActions, sortParam } from '../../table'
import { selectSorting } from '../../table/selectors'

import { ImportCheckProductsActions } from './actions'
import { getProductsImportId } from './selectors'
import { ProductItems } from './types'

type ImportResponse = {
  data: ProductItems
  meta: {
    totalCount: number
    pageCount: number
    pageNumber: number
  }
}

export function* fetchImports(action: ReturnType<typeof ImportCheckProductsActions.fetchData>) {
  yield call(waitForConfigLoad)
  const { payload } = action

  const sorting: Dictionary<ColumnSorting> = yield select(selectSorting, { tableKey: 'importCheckedProducts' })

  const { data }: AxiosResponse<ImportResponse> = yield call(apiCall, {
    url: `/api/import/${payload.importId}/items`,
    method: 'get',
    params: {
      page: payload.page,
      pageSize: payload.pageSize,
      order: sortParam(sorting)
    }
  })

  yield put(
    ImportCheckProductsActions.setData({
      data: data.data,
      page: data.meta.pageNumber,
      totalPages: data.meta.totalCount
    })
  )

  if (data.meta.pageNumber === 1) {
    yield put(
      TableActions.setPagination({
        tableKey: 'importCheckedProducts',
        pagination: {
          current: data.meta.pageNumber,
          pageSize: payload.pageSize,
          total: data.meta.totalCount
        }
      })
    )
  }
}

export function* reloadOnSort(action: ReturnType<typeof TableActions.setSorting>) {
  if (action.payload.tableKey !== 'importCheckedProducts') return

  const importId: string = yield select(getProductsImportId)
  const pageSize: number = yield select(getAccountReportsPageSize)

  yield put(
    ImportCheckProductsActions.fetchData({
      importId,
      page: 1,
      pageSize
    })
  )
}

export function* loadOnPageChange({ payload }: ReturnType<typeof TableActions.setPagination>) {
  if (payload.tableKey !== 'importCheckedProducts') return

  const importId: string = yield select(getProductsImportId)
  const { pagination } = payload

  yield put(
    ImportCheckProductsActions.fetchData({
      importId,
      page: pagination.current,
      pageSize: pagination.pageSize
    })
  )
}

/* istanbul ignore next */
export function* importCheckProductsSagas() {
  yield all([
    takeLatest(getType(ImportCheckProductsActions.fetchData), withLoading('importCheckProducts', fetchImports)),
    takeLatest(getType(TableActions.setSorting), reloadOnSort),
    takeLatest(getType(TableActions.changePagination), loadOnPageChange)
  ])
}
